/**
 * How each stage action LOOKS. Which actions exist is decided by
 * `src/lib/dealflow/stage.ts`; this file only dresses them.
 *
 * Separated so the card renders `offeredActions(stage).map(...)` and cannot
 * quietly grow an opinion about what is legal — the exact drift that left a
 * card offering "Interested" to a deal already marked interested.
 *
 * Labels are mandatory. Many of these actions are not icon-obvious, and a row
 * mixing icon-only and labelled buttons reads as broken.
 *
 * This map is DATA, not components: it holds icon NAMES, never a `LucideIcon`
 * value. `lucide-react-native`'s CJS entry eagerly requires every icon file,
 * each of which imports `react-native-svg` → RN's `View` → `TurboModuleRegistry`
 * at import time — fatal under this repo's `testEnvironment: 'node'` jest
 * config. A plain data module must stay import-safe under jest without a
 * global mock. The two `.tsx` consumers (`DealflowCard.tsx`,
 * `src/app/startup-detail.tsx`) already import `lucide-react-native`
 * successfully as RN components, so they resolve the name to a component
 * locally.
 */
import type { DealAction } from '@/lib/dealflow/stage';
import type { CardActionVariant } from './CardActionButton';

export type CardActionIcon = 'chevron-right' | 'eye' | 'file-search' | 'key-round' | 'rotate-ccw' | 'star';

export interface CardActionPresentation {
  label: string;
  variant: CardActionVariant;
  icon?: CardActionIcon;
}

export const CARD_ACTION_PRESENTATION: Record<DealAction, CardActionPresentation> = {
  reject:            { label: 'Pass',        variant: 'neutral' },
  'mark-interested': { label: 'Interested',  variant: 'success', icon: 'star' },
  monitor:           { label: 'Monitor',     variant: 'neutral', icon: 'eye' },
  // "Request access", not "Diligence": the button asks the founder to open the
  // data room. Labelling it as entering diligence promised a workspace the tap
  // could not open, because only the founder's approval gets you in.
  'request-access':  { label: 'Request access', variant: 'primary', icon: 'key-round' },
  'open-diligence':  { label: 'Open DD',     variant: 'primary', icon: 'file-search' },
  're-engage':       { label: 'Re-engage',   variant: 'success', icon: 'rotate-ccw' },
  view:              { label: 'View',        variant: 'primary', icon: 'chevron-right' },
};
